import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FolderInput, RefreshCw, Upload } from 'lucide-react';
import { rowsOf, useApi } from '@/Hooks/useApi';
import { usePermission } from '@/Hooks/usePermission';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Field, Select } from '@/components/ui/field';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states';
import { Table, TD, TH, THead, TR, TableEmpty, TableWrap } from '@/components/ui/table';
import ImportWizard from './ImportWizard';
import {
  formatBytes,
  formatDateTime,
  localize,
  toneForJobStatus,
  type DatasetsResponse,
  type ImportDatasetId,
  type ImportJobRow,
  type ImportJobStatus,
} from './lib';

const STATUSES: ImportJobStatus[] = [
  'UPLOADED',
  'VALIDATED',
  'INVALID',
  'COMMITTING',
  'COMMITTED',
  'PARTIALLY_COMMITTED',
  'FAILED',
  'CANCELLED',
];

const PAGE_SIZE = 25;

/**
 * ADR-0007 — the Migration Center.
 *
 * Not the catalog's own importer at `/catalog/import` (`ImportDialog.tsx`),
 * which stays exactly as it is: a rich, Arabic-aware product-list tool with
 * sizes, per-locale columns and menu linking, already in production. This
 * screen is the general onboarding path underneath it — a plain product list
 * plus customers, suppliers, inventory items and opening stock, each with a
 * mapping step, a dry run that writes nothing, a downloadable error report and
 * a job history that survives closing the tab. `docs/migration-center.md`
 * (backend) is the operator-facing version of the same distinction.
 *
 * History first, wizard second: the job record is what makes a half-finished
 * import safe to walk away from and come back to, so the list of past and
 * in-progress jobs is the anchor, not an afterthought bolted onto an upload
 * dialog.
 */
export default function Imports() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const { can } = usePermission();

  const [dataset, setDataset] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [wizard, setWizard] = useState<{ jobId: string | null } | null>(null);

  const params = useMemo(
    () => ({
      ...(dataset ? { dataset } : {}),
      ...(status ? { status } : {}),
      page,
      limit: PAGE_SIZE,
    }),
    [dataset, status, page],
  );

  const { data, loading, error, reload } = useApi<unknown>('/imports', params);
  const { rows, total } = useMemo(() => rowsOf<ImportJobRow>(data), [data]);
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const specs = useApi<DatasetsResponse>('/imports/datasets');
  const datasetLabel = (id: ImportDatasetId) => {
    const spec = specs.data?.datasets.find((d) => d.dataset === id);
    return spec ? localize(spec.label, spec.labelAr, lang) : id;
  };

  const filtered = dataset || status;

  const closeWizard = () => setWizard(null);
  const onChanged = () => reload();

  const canStart = can('imports.create');

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-xl font-semibold">
            <FolderInput className="h-5 w-5" />
            {t('imports.title')}
          </h1>
          <p className="text-sm text-ink-muted">{t('imports.subtitle')}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={reload} disabled={loading}>
            <RefreshCw className={loading ? 'h-4 w-4 animate-spin' : 'h-4 w-4'} />
            {t('common.refresh')}
          </Button>
          <Button
            size="sm"
            onClick={() => setWizard({ jobId: null })}
            disabled={!canStart}
            title={canStart ? undefined : t('imports.needsCreatePermission')}
          >
            <Upload className="h-4 w-4" />
            {t('imports.start')}
          </Button>
        </div>
      </header>

      <Card>
        <CardContent className="pt-5">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Field label={t('imports.filters.dataset')}>
              <Select
                value={dataset}
                onChange={(e) => {
                  setDataset(e.target.value);
                  setPage(1);
                }}
              >
                <option value="">{t('imports.filters.allDatasets')}</option>
                {(specs.data?.datasets ?? []).map((d) => (
                  <option key={d.dataset} value={d.dataset}>
                    {localize(d.label, d.labelAr, lang)}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label={t('imports.filters.status')}>
              <Select
                value={status}
                onChange={(e) => {
                  setStatus(e.target.value);
                  setPage(1);
                }}
              >
                <option value="">{t('imports.filters.allStatuses')}</option>
                {STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {t(`imports.status.${s}`)}
                  </option>
                ))}
              </Select>
            </Field>
          </div>
        </CardContent>
      </Card>

      {loading && !rows.length ? (
        <LoadingState label={t('imports.loading')} />
      ) : error ? (
        // A failed read must not look like an empty history — RLS answers an
        // unbound tenant read with zero rows, not an error, so the two have to
        // stay visually distinct or a broken screen reads as "nothing here yet".
        <ErrorState message={error} onRetry={reload} />
      ) : (
        <>
          <TableWrap>
            <Table>
              <THead>
                <TR>
                  <TH>{t('imports.table.dataset')}</TH>
                  <TH>{t('imports.table.file')}</TH>
                  <TH>{t('imports.table.status')}</TH>
                  <TH className="text-end">{t('imports.table.rows')}</TH>
                  <TH className="text-end">{t('imports.table.issues')}</TH>
                  <TH>{t('imports.table.createdBy')}</TH>
                  <TH>{t('imports.table.createdAt')}</TH>
                  <TH />
                </TR>
              </THead>
              <tbody>
                {!rows.length && (
                  <TableEmpty colSpan={8}>
                    {filtered ? (
                      <EmptyState
                        title={t('imports.empty.filteredTitle')}
                        hint={t('imports.empty.filteredHint')}
                        action={
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setDataset('');
                              setStatus('');
                              setPage(1);
                            }}
                          >
                            {t('imports.filters.clear')}
                          </Button>
                        }
                      />
                    ) : (
                      <EmptyState
                        title={t('imports.empty.title')}
                        hint={t('imports.empty.hint')}
                        action={
                          canStart ? (
                            <Button size="sm" onClick={() => setWizard({ jobId: null })}>
                              <Upload className="h-4 w-4" />
                              {t('imports.start')}
                            </Button>
                          ) : undefined
                        }
                      />
                    )}
                  </TableEmpty>
                )}

                {rows.map((job) => {
                  const issues = job.errorCount + job.warningCount;
                  return (
                    <TR
                      key={job.id}
                      className="cursor-pointer"
                      onClick={() => setWizard({ jobId: job.id })}
                    >
                      <TD>{datasetLabel(job.dataset)}</TD>
                      <TD className="max-w-[220px] truncate" title={job.fileName}>
                        {job.fileName}
                        <div className="text-xs text-ink-muted">{formatBytes(job.fileSize)}</div>
                      </TD>
                      <TD>
                        <Badge tone={toneForJobStatus(job.status)}>
                          {t(`imports.status.${job.status}`)}
                        </Badge>
                      </TD>
                      <TD className="text-end tabular-nums">{job.rowCount}</TD>
                      <TD className="text-end tabular-nums">
                        {issues > 0 ? (
                          <span className="text-amber-700 dark:text-amber-400">{issues}</span>
                        ) : (
                          '—'
                        )}
                      </TD>
                      <TD>
                        {job.createdBy
                          ? [job.createdBy.firstName, job.createdBy.lastName].filter(Boolean).join(' ') ||
                            '—'
                          : '—'}
                      </TD>
                      <TD className="whitespace-nowrap text-ink-muted">
                        {formatDateTime(job.createdAt, lang)}
                      </TD>
                      <TD>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            setWizard({ jobId: job.id });
                          }}
                        >
                          {t('imports.table.open')}
                        </Button>
                      </TD>
                    </TR>
                  );
                })}
              </tbody>
            </Table>
          </TableWrap>

          {total > 0 && (
            <div className="flex items-center justify-between gap-3 text-sm">
              <span className="text-ink-muted">{t('imports.table.count', { count: total })}</span>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1 || loading}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  {t('common.back')}
                </Button>
                <span className="tabular-nums">{t('imports.table.pageOf', { page, pages })}</span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= pages || loading}
                  onClick={() => setPage((p) => p + 1)}
                >
                  {t('common.next')}
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {wizard && (
        <ImportWizard open onClose={closeWizard} jobId={wizard.jobId} onChanged={onChanged} />
      )}
    </div>
  );
}
